import React, { useState, useMemo, useRef, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useRole } from '../hooks/useRole';
import { useData } from '../context/DataContext';
import { useTheme } from '../context/ThemeContext';
import { sortProductsFlat } from '../utils/inventorySorting';
import Sidebar, { SIDEBAR_W } from '../components/Sidebar';

const CATEGORIES = [
  'Chain Link',
  'Galv Fittings',
  'Black Fittings',
  'Galv Pipe',
  'Black Pipe',
  'Wood',
  'Wood Hardware',
  'Split Rail',
  'Tools/Misc',
  'PVC',
  'Cantilever Gate',
  'Aluminum Slide Gate Hardware',
  'Aluminum'
];

const LEVEL_LABELS = { A: 'Standard Retail', B: 'Preferred Customer', C: 'Contractor Price' };

function PriceList() {
  const navigate = useNavigate();
  const { isAdmin } = useRole();
  const { products, loadingProducts: loading, refreshProducts } = useData();
  const { theme, themeName } = useTheme();
  const isDark = themeName === 'dark';

  // Flat blue palette — overrides the dark theme vars. No logic depends on theme.
  // Semantic greens/ambers/reds keep meaning; `${C_X}26/4D/0D` appends downstream
  // become valid 8-digit-hex tints of these blues/semantics.
  const C_TEXT = '#03045E';
  const C_TEXT_2 = '#6B7C93';
  const C_TEXT_3 = '#6B7C93';
  const C_BORDER = '#E3ECF5';
  const C_BG_CARD = '#FFFFFF';
  const C_BG_CARD_SOLID = '#FFFFFF';
  const C_BG_NESTED = '#CDE7F5';
  const C_CYAN = '#0077B6';
  const C_CYAN_BRIGHT = '#0077B6';
  const C_VIOLET = '#0096C7';
  const C_VIOLET_BRIGHT = '#0096C7';
  const C_GREEN = '#0F6E56';
  const C_GREEN_BRIGHT = '#0F6E56';
  const C_ORANGE = '#854F0B';
  const C_ORANGE_BRIGHT = '#854F0B';
  const C_RED_BRIGHT = '#A32D2D';
  const GRADIENT = '#0077B6';

  const LEVEL_COLORS = {
    A: { bg: '#E6F1FB', border: '#0077B6', text: '#0077B6' },
    B: { bg: '#E6F7EE', border: '#0F6E56', text: '#0F6E56' },
    C: { bg: '#FDF1DD', border: '#854F0B', text: '#854F0B' },
  };

  const cyanGlassButton = {
    background: '#E6F1FB',
    border: '1px solid #0077B6',
    color: '#0077B6',
    padding: '10px 18px',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.3s, border-color 0.3s',
  };

  const violetGlassButton = {
    background: '#E0F7FA',
    border: '1px solid #0096C7',
    color: '#0096C7',
    padding: '10px 18px',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.3s, border-color 0.3s',
  };

  const greenGlassButton = {
    background: '#E6F7EE',
    border: '1px solid #0F6E56',
    color: '#0F6E56',
    padding: '10px 18px',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.3s',
  };

  const neutralButton = {
    background: '#FFFFFF',
    border: `1px solid ${C_BORDER}`,
    color: C_TEXT,
    padding: '10px 18px',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.3s',
  };
  const neutralHover = '#CDE7F5';
  const neutralBg = neutralButton.background;

  const gradientButton = {
    background: '#0077B6',
    border: 'none',
    color: '#fff',
    padding: '12px 24px',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    letterSpacing: 0.5,
    boxShadow: '0 4px 14px rgba(0,119,182,0.3)',
    transition: 'transform 0.3s, box-shadow 0.3s',
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    background: '#FFFFFF',
    border: `1px solid ${C_BORDER}`,
    borderRadius: 10,
    color: C_TEXT,
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.3s, box-shadow 0.3s, background 0.3s',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };

  const readOnlyDisplay = {
    width: '100%',
    padding: '12px 16px',
    background: '#CDE7F5',
    border: `1px solid ${C_BORDER}`,
    borderRadius: 10,
    color: C_TEXT_2,
    fontSize: 14,
    boxSizing: 'border-box',
  };

  const labelStyle = {
    color: C_TEXT_2,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 8,
    display: 'block',
  };

  const headerCellStyle = {
    padding: '14px 16px',
    textAlign: 'left',
    color: '#6B7C93',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
    borderBottom: '1px solid #E3ECF5',
  };

  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [selectedLevel, setSelectedLevel] = useState('A');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState({
    sku: '', description: '', category: CATEGORIES[0], unit: 'pc',
    costPrice: '', markupA: '', markupB: '', markupC: '',
    isStock: true, isActive: true,
  });
  const skuInputRef = useRef(null);

  // Focus the SKU field when the modal opens with an empty SKU — i.e. a new
  // product or a clone, where the user must assign a unique SKU first.
  useEffect(() => {
    if (showForm && isAdmin && !form.sku && skuInputRef.current) {
      const t = setTimeout(() => skuInputRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [showForm, form.sku, isAdmin]);

  // Back-derive the markup multiplier from a stored selling price: price ÷ cost.
  // Returns '' when cost is 0 (no recoverable multiplier). 6 decimals keeps
  // enough precision that re-saving an untouched field reproduces the stored
  // price to the cent, while trimming binary float noise (e.g. 2.3000000000004).
  const deriveMarkup = (price, cost) => {
    const p = parseFloat(price) || 0;
    const c = parseFloat(cost) || 0;
    if (c <= 0) return '';
    return String(parseFloat((p / c).toFixed(6)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // SKU is required and must be unique across products (excluding the one
    // currently being edited).
    const trimmedSku = form.sku.trim();
    if (!trimmedSku) {
      alert('SKU is required.');
      return;
    }
    const skuExists = products.some(p =>
      p.id !== editingProduct?.id &&
      String(p.sku ?? '').trim().toLowerCase() === trimmedSku.toLowerCase()
    );
    if (skuExists) {
      alert(`❌ SKU "${trimmedSku}" is already in use by another product. Please choose a unique SKU.`);
      return;
    }

    const cost = parseFloat(form.costPrice) || 0;

    // Cost of 0 makes every selling price compute to $0.00 (cost × markup).
    // Warn but don't block — the user may be staging a product before costing it.
    if (cost <= 0) {
      const ok = window.confirm(
        '⚠️ Cost Price is 0. All selling prices (A, B, C) will be saved as $0.00.\n\nSave anyway?'
      );
      if (!ok) return;
    }

    // Markup is now the input: sellingPrice = cost × multiplier. Empty/NaN
    // markup is treated as no price (0), never NaN.
    const markupA = parseFloat(form.markupA) || 0;
    const markupB = parseFloat(form.markupB) || 0;
    const markupC = parseFloat(form.markupC) || 0;
    const round2 = (n) => Math.round(n * 100) / 100;
    const priceA = round2(cost * markupA);
    const priceB = round2(cost * markupB);
    const priceC = round2(cost * markupC);
    const data = {
      sku: trimmedSku,
      description: form.description,
      category: form.category,
      unit: form.unit,
      costPrice: cost,
      priceA, priceB, priceC,
      // markupA/B/C are write-only (never read anywhere in the app). They used
      // to store a PERCENTAGE ((price-cost)/cost*100); they now store the
      // MULTIPLIER the user typed (sellingPrice = cost × multiplier).
      markupA, markupB, markupC,
      isStock: form.isStock,
      isActive: form.isActive,
    };
    try {
      if (editingProduct) {
        await updateDoc(doc(db, 'products', editingProduct.id), data);
      } else {
        await addDoc(collection(db, 'products'), { ...data, createdAt: new Date().toISOString() });
      }
      setForm({ sku: '', description: '', category: CATEGORIES[0], unit: 'pc', costPrice: '', markupA: '', markupB: '', markupC: '', isStock: true, isActive: true });
      setShowForm(false);
      setEditingProduct(null);
      refreshProducts();
    } catch (err) {
      console.error('Error saving product:', err);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    // Existing products store only priceA/B/C. Back-derive the markup multiplier
    // (price ÷ cost) to populate the now-editable markup inputs.
    const cost = product.costPrice ?? '';
    setForm({
      sku: product.sku || '',
      description: product.description || '',
      category: product.category || CATEGORIES[0],
      unit: product.unit || 'pc',
      costPrice: cost,
      markupA: deriveMarkup(product.priceA, cost),
      markupB: deriveMarkup(product.priceB, cost),
      markupC: deriveMarkup(product.priceC, cost),
      isStock: !!product.isStock,
      isActive: product.isActive !== false,
    });
    setShowForm(true);
  };

  const openNew = () => {
    setEditingProduct(null);
    setForm({ sku: '', description: '', category: CATEGORIES[0], unit: 'pc', costPrice: '', markupA: '', markupB: '', markupC: '', isStock: true, isActive: true });
    setShowForm(true);
  };

  // Clone a product: pre-fill the New Product form with a copy of its data but
  // an empty SKU. Nothing is written to Firestore until the user saves, so a
  // cancelled clone leaves no orphaned record behind.
  const handleClone = (product) => {
    setEditingProduct(null);
    const cost = product.costPrice ?? '';
    setForm({
      sku: '',
      description: product.description || '',
      category: product.category || CATEGORIES[0],
      unit: product.unit || 'pc',
      costPrice: cost,
      markupA: deriveMarkup(product.priceA, cost),
      markupB: deriveMarkup(product.priceB, cost),
      markupC: deriveMarkup(product.priceC, cost),
      isStock: !!product.isStock,
      isActive: true,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingProduct(null);
  };

  // Category values actually present in the data (trimmed). Gate products are
  // imported straight from the CSV (CATEGORY_MAP is an identity map with a
  // fallthrough), so their stored category strings may not match the hardcoded
  // CATEGORIES list. Used to surface any non-canonical (legacy/imported)
  // categories in the dropdowns so no stored value is ever hidden.
  const dataCategories = useMemo(() => {
    const present = new Set();
    products.forEach(p => { if (p.category) present.add(p.category.trim()); });
    return present;
  }, [products]);

  const extraCategories = useMemo(
    () => [...dataCategories].filter(c => !CATEGORIES.includes(c)).sort(),
    [dataCategories]
  );

  // Filter dropdown: full canonical list plus any non-canonical categories found
  // in the data — identical to the form's list, so every category the form offers
  // is filterable even when it has no products yet (e.g. "Aluminum Slide Gate
  // Hardware"). Selecting an empty category simply shows no matches.
  const availableCategories = useMemo(
    () => [...CATEGORIES, ...extraCategories],
    [extraCategories]
  );

  // Form dropdown: all canonical options plus any legacy categories present in
  // the data, so editing a product never silently drops a non-canonical value.
  const formCategories = useMemo(
    () => [...CATEGORIES, ...extraCategories],
    [extraCategories]
  );

  const filteredProducts = sortProductsFlat(products.filter(p => {
    const matchSearch =
      p.description?.toLowerCase().includes(search.toLowerCase()) ||
      p.sku?.toLowerCase().includes(search.toLowerCase());
    const matchCategory = filterCategory === 'All' || (p.category || '').trim() === filterCategory;
    return matchSearch && matchCategory;
  }));

  // Export ALL products (ignores search/category filters) to a clean RFC-4180 CSV.
  // Admin-only (button is gated) — the file includes Cost Price. Read-only: builds a
  // string and triggers a browser download; touches no product or Firestore data.
  const handleExportCSV = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const yesNo = (b) => (b ? 'Yes' : 'No');
    const headers = ['SKU', 'Description', 'Category', 'Unit', 'Cost Price', 'Price A', 'Price B', 'Price C', 'Stock Item', 'Active'];
    const rows = products.map(p => [
      esc(p.sku),
      esc(p.description),
      esc(p.category),
      esc(p.unit),
      esc(p.costPrice ?? 0),
      esc(p.priceA ?? 0),
      esc(p.priceB ?? 0),
      esc(p.priceC ?? 0),
      esc(yesNo(p.isStock)),
      esc(yesNo(p.isActive)),
    ].join(','));
    // Prepend a UTF-8 BOM so Excel reads accented characters correctly.
    const csv = '﻿' + [headers.map(esc).join(','), ...rows].join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'atlantic-products.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const focusInputCyan = (e) => {
    e.target.style.borderColor = '#0077B6';
    e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)';
  };
  const blurInputDefault = (e) => {
    e.target.style.borderColor = C_BORDER;
    e.target.style.boxShadow = 'none';
  };

  return (
    <>
      <Sidebar />
      <div style={{
        minHeight: '100vh',
        marginLeft: SIDEBAR_W,
        background: '#CDE7F5',
        padding: 24,
        color: C_TEXT,
        transition: 'background 0.3s ease, color 0.3s ease',
      }}>
      <style>{`
        select.dark-select option { background: #FFFFFF; color: #03045E; }
        .product-row:hover { background: #E6F1FB !important; }
      `}</style>

      <div style={{ maxWidth: 1280, margin: '0 auto' }}>

        {/* ── Header ── */}
        <div style={{
          background: C_BG_CARD,
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          border: `1px solid ${C_BORDER}`,
          borderRadius: 16,
          padding: '20px 24px',
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div>
              <h1 style={{
                fontSize: 22, fontWeight: 800, margin: 0,
                color: C_TEXT,
              }}>
                Price List
              </h1>
              <p style={{
                color: C_TEXT_2, fontSize: 12, margin: 0,
                letterSpacing: 1, textTransform: 'uppercase', fontWeight: 600,
              }}>
                Product Catalog & Pricing
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={async () => { await refreshProducts(); alert('✅ Products refreshed from Firestore'); }}
              style={neutralButton}
              onMouseEnter={e => { e.currentTarget.style.background = neutralHover; }}
              onMouseLeave={e => { e.currentTarget.style.background = neutralBg; }}
            >
              🔄 Refresh
            </button>
            {isAdmin && (
              <button
                onClick={() => navigate('/import-products')}
                style={greenGlassButton}
                onMouseEnter={e => { e.currentTarget.style.background = `${C_GREEN}2E`; }}
                onMouseLeave={e => { e.currentTarget.style.background = `${C_GREEN}1A`; }}
              >
                📥 Import CSV
              </button>
            )}
            {isAdmin && (
              <button
                onClick={handleExportCSV}
                style={cyanGlassButton}
                onMouseEnter={e => { e.currentTarget.style.background = `${C_CYAN}2E`; }}
                onMouseLeave={e => { e.currentTarget.style.background = cyanGlassButton.background; }}
              >
                📤 Export CSV
              </button>
            )}
          </div>
        </div>

        {/* ── Price Level Selector ── */}
        <div style={{
          background: C_BG_CARD,
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          border: `1px solid ${C_BORDER}`,
          borderRadius: 16,
          padding: 12,
          marginBottom: 16,
          display: 'flex',
          gap: 8,
        }}>
          {['A', 'B', 'C'].map(level => {
            const isActive = selectedLevel === level;
            const c = LEVEL_COLORS[level];
            return (
              <button
                key={level}
                onClick={() => setSelectedLevel(level)}
                style={{
                  flex: 1,
                  padding: '14px 20px',
                  background: isActive ? c.bg : C_BG_NESTED,
                  border: `1px solid ${isActive ? c.border : C_BORDER}`,
                  borderRadius: 12,
                  color: isActive ? c.text : C_TEXT_3,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'background 0.3s, border-color 0.3s, color 0.3s',
                }}
              >
                <span style={{ fontSize: 16 }}>Level {level}</span>
                <span style={{
                  display: 'block', fontSize: 11, fontWeight: 500,
                  letterSpacing: 0.5, opacity: 0.8, marginTop: 2,
                }}>
                  {LEVEL_LABELS[level]}
                </span>
              </button>
            );
          })}
        </div>

        {/* ── Search + filter ── */}
        <div style={{
          background: C_BG_CARD,
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          border: `1px solid ${C_BORDER}`,
          borderRadius: 16,
          padding: 20,
          marginBottom: 20,
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, position: 'relative', minWidth: 260 }}>
            <input
              type="text"
              placeholder="Search by description or SKU..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...inputStyle, paddingLeft: 44 }}
              onFocus={focusInputCyan}
              onBlur={blurInputDefault}
            />
            <span style={{
              position: 'absolute', left: 16, top: '50%',
              transform: 'translateY(-50%)',
              color: C_TEXT_3, fontSize: 16, pointerEvents: 'none',
            }}>🔍</span>
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="dark-select"
            style={{ ...inputStyle, width: 'auto', minWidth: 200, cursor: 'pointer' }}
          >
            <option value="All">All Categories</option>
            {availableCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
          {isAdmin && (
            <button
              onClick={openNew}
              style={gradientButton}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 8px 30px ${C_CYAN}80`;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 4px 20px ${C_CYAN}4D`;
              }}
            >
              + New Product
            </button>
          )}
        </div>

        {/* ── List ── */}
        {loading ? (
          <div style={{
            background: C_BG_CARD,
            border: `1px dashed ${C_BORDER}`,
            borderRadius: 16,
            padding: '60px 20px',
            textAlign: 'center',
          }}>
            <p style={{ color: C_TEXT_3, fontSize: 16, margin: 0 }}>Loading products…</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div style={{
            background: C_BG_CARD,
            border: `1px dashed ${C_BORDER}`,
            borderRadius: 16,
            padding: '60px 20px',
            textAlign: 'center',
          }}>
            <p style={{ color: C_TEXT_3, fontSize: 16, marginBottom: 8 }}>No products found</p>
            <p style={{ color: C_TEXT_3, fontSize: 13, margin: 0 }}>
              Try a different search or import products from CSV
            </p>
          </div>
        ) : (
          <div style={{
            background: C_BG_CARD,
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
            border: `1px solid ${C_BORDER}`,
            borderRadius: 16,
            overflow: 'hidden',
          }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#E6F1FB' }}>
                    <th style={headerCellStyle}>SKU</th>
                    <th style={headerCellStyle}>Description</th>
                    <th style={{ ...headerCellStyle, textAlign: 'center' }}>Category</th>
                    <th style={{ ...headerCellStyle, textAlign: 'center' }}>Unit</th>
                    {isAdmin && (
                      <th style={{ ...headerCellStyle, textAlign: 'right' }}>Cost</th>
                    )}
                    <th style={{ ...headerCellStyle, textAlign: 'right' }}>Price A</th>
                    <th style={{ ...headerCellStyle, textAlign: 'right' }}>Price B</th>
                    <th style={{ ...headerCellStyle, textAlign: 'right' }}>Price C</th>
                    <th style={{ ...headerCellStyle, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product, index) => (
                    <tr
                      key={product.id}
                      className="product-row"
                      style={{
                        background: index % 2 === 0 ? 'transparent' : '#CDE7F5',
                        borderBottom: `1px solid ${C_BORDER}`,
                        transition: 'background 0.2s',
                      }}
                    >
                      <td style={{
                        padding: '12px 16px',
                        color: C_CYAN_BRIGHT,
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        fontSize: 12,
                      }}>
                        {product.sku}
                      </td>
                      <td style={{ padding: '12px 16px', color: C_TEXT, fontSize: 13 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span>{product.description}</span>
                          {product.isStock === false && (
                            <span style={{
                              background: `${C_ORANGE}26`,
                              color: C_ORANGE_BRIGHT,
                              padding: '2px 8px',
                              borderRadius: 4,
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: 0.5,
                            }}>
                              NON-STOCK
                            </span>
                          )}
                          {product.isActive === false && (
                            <span style={{
                              background: '#FBECEC',
                              color: C_RED_BRIGHT,
                              padding: '2px 8px',
                              borderRadius: 4,
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: 0.5,
                            }}>
                              INACTIVE
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <span style={{
                          background: '#E6F1FB',
                          color: '#0077B6',
                          padding: '3px 10px',
                          borderRadius: 6,
                          fontSize: 11,
                          fontWeight: 600,
                        }}>
                          {product.category}
                        </span>
                      </td>
                      <td style={{
                        padding: '12px 16px', textAlign: 'center',
                        color: C_TEXT_2, fontSize: 12,
                      }}>
                        {product.unit}
                      </td>
                      {isAdmin && (
                        <td style={{
                          padding: '12px 16px', textAlign: 'right',
                          color: C_ORANGE_BRIGHT, fontWeight: 600, fontSize: 13,
                        }}>
                          ${parseFloat(product.costPrice || 0).toFixed(2)}
                        </td>
                      )}
                      <td style={{
                        padding: '12px 16px', textAlign: 'right',
                        color: selectedLevel === 'A' ? C_CYAN_BRIGHT : C_TEXT_2,
                        fontWeight: selectedLevel === 'A' ? 700 : 500,
                        fontSize: 13,
                      }}>
                        ${parseFloat(product.priceA || 0).toFixed(2)}
                      </td>
                      <td style={{
                        padding: '12px 16px', textAlign: 'right',
                        color: selectedLevel === 'B' ? '#0077B6' : C_TEXT_2,
                        fontWeight: selectedLevel === 'B' ? 700 : 500,
                        fontSize: 13,
                      }}>
                        ${parseFloat(product.priceB || 0).toFixed(2)}
                      </td>
                      <td style={{
                        padding: '12px 16px', textAlign: 'right',
                        color: selectedLevel === 'C' ? '#0077B6' : C_TEXT_2,
                        fontWeight: selectedLevel === 'C' ? 700 : 500,
                        fontSize: 13,
                      }}>
                        ${parseFloat(product.priceC || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                          <button
                            onClick={() => handleEdit(product)}
                            style={{ ...cyanGlassButton, padding: '6px 12px', fontSize: 11 }}
                            onMouseEnter={e => { e.currentTarget.style.background = `${C_CYAN}2E`; }}
                            onMouseLeave={e => { e.currentTarget.style.background = `${C_CYAN}1A`; }}
                          >
                            {isAdmin ? '✏️ Edit' : '👁 View'}
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => handleClone(product)}
                              title="Create a copy of this product"
                              style={{ ...violetGlassButton, padding: '6px 12px', fontSize: 11 }}
                              onMouseEnter={e => { e.currentTarget.style.background = `${C_VIOLET}2E`; }}
                              onMouseLeave={e => { e.currentTarget.style.background = `${C_VIOLET}1A`; }}
                            >
                              📋 Clone
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Results counter */}
        {!loading && (
          <div style={{ marginTop: 16, textAlign: 'center', color: C_TEXT_2, fontSize: 13 }}>
            Showing <span style={{ color: C_CYAN_BRIGHT, fontWeight: 700 }}>{filteredProducts.length}</span> of {products.length} products
          </div>
        )}
      </div>

      {/* ── Form Modal ── */}
      {showForm && (
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(3, 4, 94, 0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 50,
            padding: 20,
          }}
          onClick={closeForm}
        >
          <div
            style={{
              background: C_BG_CARD_SOLID,
              border: `1px solid ${C_BORDER}`,
              borderRadius: 20,
              padding: 32,
              width: '100%',
              maxWidth: 900,
              maxHeight: '92vh',
              overflowY: 'auto',
              boxShadow: '0 20px 50px rgba(3,4,94,0.18)',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: C_TEXT, margin: 0 }}>
                {editingProduct ? '✏️ Edit Product' : '+ New Product'}
              </h2>
              {!isAdmin && (
                <span style={{
                  background: `${C_ORANGE}26`,
                  color: C_ORANGE_BRIGHT,
                  border: `1px solid ${C_ORANGE}4D`,
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '4px 10px',
                  borderRadius: 6,
                  letterSpacing: 0.5,
                }}>
                  VIEW ONLY — Some fields are admin-only
                </span>
              )}
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>SKU <span style={{ color: C_RED_BRIGHT }}>*</span></label>
                  <input
                    ref={skuInputRef}
                    type="text"
                    value={form.sku}
                    onChange={(e) => setForm({ ...form, sku: e.target.value })}
                    required
                    placeholder="Enter unique SKU"
                    style={inputStyle}
                    onFocus={focusInputCyan}
                    onBlur={blurInputDefault}
                  />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={labelStyle}>
                    Description <span style={{ color: C_RED_BRIGHT }}>*</span>
                    {!isAdmin && <span style={{ color: C_TEXT_3, marginLeft: 8, textTransform: 'none', letterSpacing: 0 }}>(admin only)</span>}
                  </label>
                  {isAdmin ? (
                    <input
                      type="text"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      required
                      style={inputStyle}
                      onFocus={focusInputCyan}
                      onBlur={blurInputDefault}
                    />
                  ) : (
                    <div style={readOnlyDisplay}>{form.description || '—'}</div>
                  )}
                </div>
                <div>
                  <label style={labelStyle}>Category <span style={{ color: C_RED_BRIGHT }}>*</span></label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="dark-select"
                    style={{ ...inputStyle, cursor: 'pointer' }}
                  >
                    {formCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Unit of Measure <span style={{ color: C_RED_BRIGHT }}>*</span></label>
                  <select
                    value={form.unit}
                    onChange={(e) => setForm({ ...form, unit: e.target.value })}
                    className="dark-select"
                    style={{ ...inputStyle, cursor: 'pointer' }}
                  >
                    {['pc', 'ft', 'roll', 'bag', 'lb', 'ea', 'lot'].map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>
                    Cost Price ($)
                    {!isAdmin && <span style={{ color: C_TEXT_3, marginLeft: 8, textTransform: 'none', letterSpacing: 0 }}>(admin only)</span>}
                  </label>
                  {isAdmin ? (
                    <input
                      type="number"
                      step="0.01"
                      value={form.costPrice}
                      onChange={(e) => setForm({ ...form, costPrice: e.target.value })}
                      required
                      style={inputStyle}
                      onFocus={focusInputCyan}
                      onBlur={blurInputDefault}
                    />
                  ) : (
                    <div style={readOnlyDisplay}>${parseFloat(form.costPrice || 0).toFixed(2)}</div>
                  )}
                </div>
              </div>

              {/* Price levels */}
              <p style={{
                color: C_TEXT_2, fontSize: 11, fontWeight: 700,
                letterSpacing: 1, textTransform: 'uppercase', marginTop: 24, marginBottom: 12,
              }}>
                Price Levels
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
                {['A', 'B', 'C'].map(level => {
                  const c = LEVEL_COLORS[level];
                  const markupKey = `markup${level}`;
                  const markupVal = form[markupKey];
                  const cost = parseFloat(form.costPrice) || 0;
                  const mult = parseFloat(markupVal);
                  const hasMarkup = !isNaN(mult);
                  // Selling price is derived; cost 0 or blank markup ⇒ $0.00.
                  const sellingPrice = cost > 0 && hasMarkup ? Math.round(cost * mult * 100) / 100 : 0;
                  const profit = sellingPrice - cost;
                  // Markup % = (multiplier − 1) × 100; em-dash when cost is 0 or
                  // markup is blank, matching the prior '—' convention.
                  const markupPct = cost > 0 && hasMarkup ? ((mult - 1) * 100).toFixed(1) : '—';
                  return (
                    <div key={level} style={{
                      background: c.bg,
                      border: `1px solid ${c.border}`,
                      borderRadius: 12,
                      padding: 16,
                    }}>
                      <p style={{ color: c.text, fontWeight: 700, fontSize: 13, margin: 0, marginBottom: 10 }}>
                        Level {level} — {LEVEL_LABELS[level]}
                      </p>
                      <label style={{ ...labelStyle, marginBottom: 4 }}>
                        Markup (×)
                        {!isAdmin && <span style={{ color: C_TEXT_3, marginLeft: 8, textTransform: 'none', letterSpacing: 0 }}>(admin only)</span>}
                      </label>
                      {isAdmin ? (
                        <input
                          type="number"
                          step="0.01"
                          value={markupVal}
                          onChange={(e) => setForm({ ...form, [markupKey]: e.target.value })}
                          onFocus={(e) => { e.target.select(); focusInputCyan(e); }}
                          onBlur={blurInputDefault}
                          placeholder="e.g. 1.78"
                          style={{ ...inputStyle, color: c.text, fontWeight: 700, fontSize: 16 }}
                        />
                      ) : (
                        <div style={{ ...readOnlyDisplay, color: c.text, fontWeight: 700, fontSize: 16 }}>
                          {hasMarkup ? `${mult}×` : '—'}
                        </div>
                      )}

                      <div style={{
                        marginTop: 10, padding: '8px 12px',
                        background: C_BG_NESTED,
                        borderRadius: 8,
                        display: 'flex', justifyContent: 'space-between',
                      }}>
                        <span style={{ color: C_TEXT_3, fontSize: 12 }}>Selling Price</span>
                        <span style={{ color: c.text, fontSize: 14, fontWeight: 700 }}>
                          ${sellingPrice.toFixed(2)}
                        </span>
                      </div>

                      <div style={{
                        marginTop: 6, padding: '8px 12px',
                        background: C_BG_NESTED,
                        borderRadius: 8,
                        display: 'flex', justifyContent: 'space-between',
                      }}>
                        <span style={{ color: C_TEXT_3, fontSize: 12 }}>Markup %</span>
                        <span style={{ color: C_TEXT_2, fontSize: 12, fontWeight: 700 }}>
                          {markupPct === '—' ? '—' : `${markupPct}%`}
                        </span>
                      </div>

                      <div style={{
                        marginTop: 6, padding: '8px 12px',
                        background: C_BG_NESTED,
                        borderRadius: 8,
                        display: 'flex', justifyContent: 'space-between',
                      }}>
                        <span style={{ color: C_TEXT_3, fontSize: 12 }}>Profit/unit</span>
                        <span style={{ color: profit >= 0 ? C_GREEN_BRIGHT : C_RED_BRIGHT, fontSize: 12, fontWeight: 700 }}>
                          ${profit.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Stock + Active */}
              <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 16px',
                  background: '#FFFFFF',
                  border: `1px solid ${C_BORDER}`,
                  borderRadius: 10,
                  cursor: 'pointer',
                  flex: 1, minWidth: 200,
                }}>
                  <input
                    type="checkbox"
                    checked={form.isStock}
                    onChange={(e) => setForm({ ...form, isStock: e.target.checked })}
                    style={{ accentColor: C_CYAN, width: 18, height: 18 }}
                  />
                  <span style={{ color: C_TEXT, fontSize: 14, fontWeight: 600 }}>Stock Item</span>
                </label>
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 16px',
                  background: '#FFFFFF',
                  border: `1px solid ${C_BORDER}`,
                  borderRadius: 10,
                  cursor: 'pointer',
                  flex: 1, minWidth: 200,
                }}>
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    style={{ accentColor: C_CYAN, width: 18, height: 18 }}
                  />
                  <span style={{ color: C_TEXT, fontSize: 14, fontWeight: 600 }}>Active</span>
                </label>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                {isAdmin && (
                  <button
                    type="submit"
                    style={{ ...gradientButton, flex: 1 }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = `0 8px 30px ${C_CYAN}80`;
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = `0 4px 20px ${C_CYAN}4D`;
                    }}
                  >
                    💾 {editingProduct ? 'Save Changes' : 'Add Product'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeForm}
                  style={{ ...neutralButton, flex: 1 }}
                  onMouseEnter={e => { e.currentTarget.style.background = neutralHover; }}
                  onMouseLeave={e => { e.currentTarget.style.background = neutralBg; }}
                >
                  {isAdmin ? 'Cancel' : 'Close'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </>
  );
}

export default PriceList;
